import { Formik, FormikProps } from "formik";
import ErrorBox from "lib/components/common/error-box";
import FlexBox from "lib/components/common/flex-box";
import LoadingBox from "lib/components/common/loading-box";
import SimpleCard from "lib/components/common/simple-card";
import SubscriptionCard from "lib/components/common/subscription-card";
import DashboardLayout from "lib/components/layouts/dashboard";
import {
  REQUIRED_FIELD_VALIDATION_SCHEMA,
  UPSERT_SUBSCRIPTION_VALIDATION_SCHEME,
} from "lib/constants/validation-constants";
// import { DUMMY_FEATURES } from "lib/data/dummy-features";
import useSubscriptions from "lib/hooks/useSubscription";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { Row, Col, Button, Form } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import UpsertFeature from "../UpsertFeature";
import useClinics from "lib/hooks/useClinics";
import { PAGE_ROUTES } from "lib/constants/page-routes";
import { EMPTY_GUID } from "lib/constants";
import { DEFAULT_SUBSCRIPTION_FORM_VALUES } from "lib/constants/default-values";
import SimpleTextbox from "lib/components/common/simple-textbox";
import SimpleDropdown from "lib/components/common/simple-dropdown";
import { getArrayOfValuesFromArray } from "lib/utils/array-utils";
import FeaturesDataTable from "lib/components/data-tables/features-data-table";

const BreadcrumbItems = [
  {
    label: "Dashboard",
    path: PAGE_ROUTES.ADMIN.Dashboard,
  },
  {
    label: "Configurations",
    path: PAGE_ROUTES.ADMIN.Configurations,
  },
  {
    label: "Subscriptions",
    path: `${PAGE_ROUTES.ADMIN.Configurations}?tab=subscriptions`,
  },
] as BreadcrumbItem[];

const UpsertSubscriptions: FC = () => {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState<boolean>(false);
  const [currentFeature, setCurrentFeature] = useState<FeatureDetails | null>(
    null
  );
  const { id } = useParams();
  const formikRef = useRef<FormikProps<any>>(null);
  const {
    clinics,
    getClinics,
    loading: clinicsLoading,
    error: clinicsError,
  } = useClinics();
  const {
    subscriptions,
    getSubscriptionById,
    updateSubscription,
    createSubscription,
    loading,
    error,
  } = useSubscriptions();

  useEffect(() => {
    getClinics(false);
  }, []);

  const isNewSubscription = useMemo(() => id === EMPTY_GUID, [id]);

  useEffect(() => {
    if (!isNewSubscription) {
      getSubscriptionById(id);
    }
  }, [id]);

  const navigateToPreviousPage = () => {
    navigate(`${PAGE_ROUTES.ADMIN.Configurations}?tab=subscriptions`);
  };

  return (
    <DashboardLayout breadcrumbItems={BreadcrumbItems}>
      <SimpleCard
        title={isNewSubscription ? "Add Subscription" : "Update Subscription"}
        isLoading={clinicsLoading}
        error={clinicsError}
      >
        {error ? (
          <ErrorBox message={error} />
        ) : (
          <Formik
            innerRef={formikRef}
            initialValues={
              subscriptions?.[0] ?? {
                ...DEFAULT_SUBSCRIPTION_FORM_VALUES,
                clinicId: clinics[0]?.id,
              }
            }
            enableReinitialize={true}
            validationSchema={UPSERT_SUBSCRIPTION_VALIDATION_SCHEME}
            onSubmit={(values: SubscriptionDetails) => {
              console.log("values", values);
              if (values.id) {
                updateSubscription(values, () => navigateToPreviousPage());
              } else {
                createSubscription(values, () => navigateToPreviousPage());
              }
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              validateForm,
              setFieldValue,
            }) => (
              <>
                {loading ? (
                  <LoadingBox message="Loading Subscription Details" />
                ) : (
                  <Row>
                    <Col sm={8}>
                      <Form onSubmit={handleSubmit}>
                        <SimpleDropdown
                          label="Select Clinic"
                          name="clinicId"
                          selectedValue={values.clinicId}
                          options={getArrayOfValuesFromArray(clinics, "name")}
                          values={getArrayOfValuesFromArray(clinics, "id")}
                          onChange={handleChange}
                          required
                        />
                        <SimpleTextbox
                          label="Title"
                          type="text"
                          name="title"
                          value={values.title}
                          onChange={handleChange}
                          required
                          validationSchema={REQUIRED_FIELD_VALIDATION_SCHEMA()}
                          error={errors?.title as string}
                        />
                        <SimpleTextbox
                          label="Sub-Title"
                          as="textarea"
                          type="text"
                          name="subTitle"
                          value={values.subTitle}
                          onChange={handleChange}
                          required
                          validationSchema={REQUIRED_FIELD_VALIDATION_SCHEMA()}
                          error={errors?.subTitle as string}
                        />

                        <Row>
                          <Col sm={12} md={6}>
                            <SimpleTextbox
                              label="Daily Price"
                              type="number"
                              name="perDayPrice"
                              value={values.perDayPrice}
                              onChange={handleChange}
                              required
                              validationSchema={REQUIRED_FIELD_VALIDATION_SCHEMA(
                                "Please provide daily price"
                              )}
                              error={errors?.perDayPrice as string}
                            />
                          </Col>

                          <Col sm={12} md={6}>
                            <SimpleTextbox
                              label="SKU"
                              type="text"
                              name="sku"
                              value={values.sku}
                              onChange={handleChange}
                              validationSchema={REQUIRED_FIELD_VALIDATION_SCHEMA()}
                              required
                              error={errors?.sku as string}
                            />
                          </Col>
                        </Row>
                        <SimpleTextbox
                          label="Description"
                          as="textarea"
                          name="description"
                          value={values.description}
                          onChange={handleChange}
                          error={errors?.description as string}
                        />

                        <fieldset className="mb-4">
                          <FeaturesDataTable
                            data={(values.features as FeatureDetails[]) ?? []}
                            onChange={(features) => {
                              setFieldValue("features", features, false);
                            }}
                          />
                        </fieldset>

                        <SimpleTextbox
                          as="textarea"
                          label="Notes"
                          name="notes"
                          value={values.notes}
                          onChange={handleChange}
                          error={errors?.notes as string}
                        />
                      </Form>
                      <FlexBox justifyContent="between">
                        <Button
                          type="button"
                          variant="outline-secondary"
                          className="w-auto"
                          onClick={navigateToPreviousPage}
                        >
                          Back
                        </Button>
                        <Button
                          type="button"
                          className="w-auto"
                          onClick={() => formikRef.current?.handleSubmit()}
                        >
                          {isNewSubscription ? "Save" : "Submit"}
                        </Button>
                      </FlexBox>
                    </Col>
                    <Col sm="4">
                      <SubscriptionCard isAddOn={false} subscription={values} />
                    </Col>
                  </Row>
                )}
                <UpsertFeature
                  featureInfo={currentFeature}
                  open={showModal}
                  onClose={(featureDetails) => {
                    if (featureDetails) {
                      const features = values.features || [];
                      setFieldValue(
                        "features",
                        [...features, featureDetails],
                        true
                      );
                    }
                    setCurrentFeature(null);
                    setShowModal(false);
                  }}
                  features={[].filter(
                    (feature) => !values.features.includes(feature)
                  )}
                />
              </>
            )}
          </Formik>
        )}
      </SimpleCard>
    </DashboardLayout>
  );
};
export default UpsertSubscriptions;
